import {
  Parser,
  SOLANA_CHAIN_REFERENCE,
  SOLANA_NATIVE_SLIP44_COIN_TYPE,
  type AssetId,
} from "@repo/caip";
import { z } from "zod";
import {
  ExecutionCorrelationIdSchema,
  ExecutionPortfolioIdSchema,
  ExecutionRequestKeySchema,
  ExpectedMarketOrderVenueSchema,
  MarketOrderAccountIdSchema,
  NonNegativeIntegerAmountSchema,
  PositiveIntegerAmountSchema,
  SpecDrivenBuyFundPlanWireSchema,
  SpecDrivenBuyFundingExplanationWireSchema,
  SpecDrivenBuyPlanBasisSchema,
} from "./requests.js";
import {
  ExecutionMarketOrderQuotePreviewSchema,
  ExecutionOperationResponseSchema,
  ExecutionSpecDrivenBuyCreatedFundingSchema,
  LiveDescriptorSchema,
} from "./responses.js";
import { lazySchema } from "@repo/lazy-schema";

/**
 * One asset-trade front door. The caller states intent only (which asset,
 * which side, how much); the engine decides the cash asset, venue, funding
 * pre-steps, minimums, dust tolerance, and market hours.
 */
export const AssetTradeSideSchema = lazySchema(() => z.enum(["buy", "sell"]));

const SOLANA_BASE58_PUBLIC_KEY = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/u;

/**
 * A tradable asset: on an EVM chain with a safe positive id, an ERC-20 token
 * or the chain's native coin (`slip44:60`); on Solana mainnet (genesis-hash
 * reference), an SPL token mint or native SOL (`slip44:501`), which the
 * Solana lane trades through the wrapped-SOL mint. EVM ids are canonicalized
 * to lowercase so retries that differ only in address casing share one intent
 * fingerprint; base58 is case-significant and is kept verbatim.
 */
export const AssetTradeAssetIdSchema = lazySchema(() =>
  z
    .string()
    .trim()
    .superRefine((assetId, ctx) => {
      try {
        const parsed = Parser.parseAssetId(assetId as AssetId);
        if (parsed.chain.namespace === "solana") {
          const spl =
            parsed.asset.namespace === "spl" &&
            SOLANA_BASE58_PUBLIC_KEY.test(parsed.asset.reference);
          const nativeSol =
            parsed.asset.namespace === "slip44" &&
            parsed.asset.reference === SOLANA_NATIVE_SLIP44_COIN_TYPE;
          if (
            parsed.chain.reference !== SOLANA_CHAIN_REFERENCE ||
            (!spl && !nativeSol)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                "assetId must identify an SPL token mint or SOL on Solana mainnet",
            });
          }
          return;
        }
        const chainId = Number(parsed.chain.reference);
        const erc20 =
          parsed.asset.namespace === "erc20" &&
          /^0x[a-fA-F0-9]{40}$/u.test(parsed.asset.reference);
        const native =
          parsed.asset.namespace === "slip44" &&
          parsed.asset.reference === "60";
        if (
          parsed.chain.namespace !== "eip155" ||
          !/^[1-9]\d*$/u.test(parsed.chain.reference) ||
          !Number.isSafeInteger(chainId) ||
          (!erc20 && !native)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "assetId must identify an EVM ERC-20 token, an EVM native coin, or a Solana SPL token or SOL",
          });
        }
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "assetId must be a parseable CAIP-19 id",
        });
      }
    })
    .transform((assetId) =>
      assetId.startsWith("solana:") ? assetId : assetId.toLowerCase(),
    ),
);

/** Buys are exact-in: `cashRaw` is the most cash the trade may debit. */
export const AssetTradeBuyAmountSchema = lazySchema(() =>
  z.object({ cashRaw: PositiveIntegerAmountSchema }).strict(),
);

/**
 * A canonical asset (`core.asset_canonical`), the identity a ticket holds
 * before the engine picks the chain and token. Lowercased so retries that
 * differ only in casing share one intent fingerprint.
 */
export const AssetTradeCanonicalAssetIdSchema = lazySchema(() =>
  z
    .string()
    .trim()
    .uuid()
    .transform((assetCanonicalId) => assetCanonicalId.toLowerCase()),
);

/**
 * A canonical buy is stated in dollars, not in a chain's cash units: the
 * chain is the engine's decision, so the caller cannot know the cash
 * decimals to scale by. Up to six fraction digits, which every supported USD
 * cash asset carries. The engine scales it after resolving the chain.
 */
export const AssetTradeCashUsdAmountSchema = lazySchema(() =>
  z
    .string()
    .trim()
    .regex(
      /^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/u,
      "cashUsd must be a decimal dollar amount with at most six fraction digits",
    )
    .refine((value) => /[1-9]/u.test(value), "cashUsd must be positive"),
);

export const AssetTradeCanonicalBuyAmountSchema = lazySchema(() =>
  z.object({ cashUsd: AssetTradeCashUsdAmountSchema }).strict(),
);

/** Sells name an exact asset amount, or everything spendable after holds. */
export const AssetTradeSellAmountSchema = lazySchema(() =>
  z.union([
    z.object({ assetRaw: PositiveIntegerAmountSchema }).strict(),
    z.object({ all: z.literal(true) }).strict(),
  ]),
);

/**
 * Engine-signed preview material (HMAC domain `atb1`). It binds the reviewed
 * portfolio, asset, side, amount, destination account, venue, cash asset,
 * funding plan, gross cap, and expiry. Create refuses a basis that fails
 * verification, has expired, or names a venue the engine no longer routes to.
 * `chainId` is the engine's numeric key for the asset chain: the eip155 id,
 * or the network id reserved for Solana mainnet.
 */
export const AssetTradePlanBasisSchema = lazySchema(() =>
  z
    .object({
      version: z.literal(1),
      expiresAt: z.number().int().nonnegative(),
      chainId: z.number().int().positive(),
      destinationAccountRef: MarketOrderAccountIdSchema,
      cashAssetId: z.string().min(1),
      venue: ExpectedMarketOrderVenueSchema,
      /** Buys: the most cash the whole trade (funding included) may debit. */
      quotedGrossCapRaw: PositiveIntegerAmountSchema.nullable(),
      /**
       * Buys that must first move cash from other accounts: the exact trade
       * spend plus the engine's signed funding plan. Null when local cash covers
       * the trade.
       */
      funding: z
        .object({
          spendRaw: PositiveIntegerAmountSchema,
          basis: SpecDrivenBuyPlanBasisSchema,
        })
        .strict()
        .nullable(),
      /**
       * Present exactly when the preview was asked for a canonical asset: the
       * concrete token and scaled cash the engine resolved for it. Create
       * trades this token and never re-resolves, so the reviewed route and
       * the executed route cannot drift.
       */
      resolution: AssetTradePlanBasisResolutionSchema.optional(),
      integrity: z.string().regex(/^atb1\.[a-f0-9]{64}$/u),
    })
    .strict(),
);

export const AssetTradePlanBasisResolutionSchema = lazySchema(() =>
  z
    .object({
      assetCanonicalId: AssetTradeCanonicalAssetIdSchema,
      assetId: AssetTradeAssetIdSchema,
      /** `cashUsd` scaled to the resolved chain's cash decimals. */
      cashRaw: PositiveIntegerAmountSchema,
    })
    .strict(),
);

const assetTradeIdentityShape = lazySchema(
  () =>
    ({
      /**
       * Webapp recovery relies on `market:<portfolioId>:<side>:<assetId>:<uuid>`
       * keys; the engine persists the key verbatim.
       */
      requestKey: ExecutionRequestKeySchema,
      correlationId: ExecutionCorrelationIdSchema.optional(),
      portfolioId: ExecutionPortfolioIdSchema,
      assetId: AssetTradeAssetIdSchema,
    }) satisfies z.ZodRawShape,
);

const refineDestinationChain = (
  input: {
    readonly assetId: string;
    readonly destinationAccountRef?: string;
  },
  ctx: z.RefinementCtx,
): void => {
  if (input.destinationAccountRef === undefined) return;
  const destination = Parser.parseAccountId(input.destinationAccountRef);
  const asset = Parser.parseAssetId(input.assetId as AssetId);
  if (
    destination.chain.namespace !== asset.chain.namespace ||
    destination.chain.reference !== asset.chain.reference
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "destination account must be on the asset chain",
      path: ["destinationAccountRef"],
    });
  }
};

/**
 * Canonical intent: the caller names the asset the way the catalog does and
 * leaves chain, token, venue and funding to the engine. Buy only; a sell
 * names the held token it sells.
 */
const assetTradeCanonicalIdentityShape = lazySchema(
  () =>
    ({
      requestKey: ExecutionRequestKeySchema,
      correlationId: ExecutionCorrelationIdSchema.optional(),
      portfolioId: ExecutionPortfolioIdSchema,
      assetCanonicalId: AssetTradeCanonicalAssetIdSchema,
    }) satisfies z.ZodRawShape,
);

/** A preview for a concrete token: the request shape before canonical intent. */
export const AssetTradeConcretePreviewRequestSchema = lazySchema(() =>
  z
    .discriminatedUnion("side", [
      z
        .object({
          ...assetTradeIdentityShape,
          side: z.literal("buy"),
          amount: AssetTradeBuyAmountSchema,
          destinationAccountRef: MarketOrderAccountIdSchema.optional(),
        })
        .strict(),
      z
        .object({
          ...assetTradeIdentityShape,
          side: z.literal("sell"),
          amount: AssetTradeSellAmountSchema,
          destinationAccountRef: MarketOrderAccountIdSchema.optional(),
        })
        .strict(),
    ])
    .superRefine(refineDestinationChain),
);

/**
 * A canonical buy preview. Without `amount` the engine only resolves: the
 * response names the chain and token it would buy (`resolvedRoute`), is not
 * submittable, and carries `amount_required`.
 */
export const AssetTradeCanonicalBuyPreviewRequestSchema = lazySchema(() =>
  z
    .object({
      ...assetTradeCanonicalIdentityShape,
      side: z.literal("buy"),
      amount: AssetTradeCanonicalBuyAmountSchema.optional(),
    })
    .strict(),
);

export const AssetTradePreviewRequestSchema = lazySchema(() =>
  z.union([
    AssetTradeConcretePreviewRequestSchema,
    AssetTradeCanonicalBuyPreviewRequestSchema,
  ]),
);

/**
 * `planBasis` is required for owner-session and capability callers. Only
 * internal-key callers (durable automation) may omit it; the engine then
 * compiles fresh and never adds funding pre-steps on their behalf.
 */
export const AssetTradeConcreteCreateRequestSchema = lazySchema(() =>
  z.discriminatedUnion("side", [
    z
      .object({
        ...assetTradeIdentityShape,
        side: z.literal("buy"),
        amount: AssetTradeBuyAmountSchema,
        planBasis: AssetTradePlanBasisSchema.optional(),
      })
      .strict(),
    z
      .object({
        ...assetTradeIdentityShape,
        side: z.literal("sell"),
        amount: AssetTradeSellAmountSchema,
        planBasis: AssetTradePlanBasisSchema.optional(),
      })
      .strict(),
  ]),
);

/**
 * A canonical buy create always carries the preview's basis: the token the
 * engine resolved lives in `planBasis.resolution`, and create trades exactly
 * that token.
 */
export const AssetTradeCanonicalBuyCreateRequestSchema = lazySchema(() =>
  z
    .object({
      ...assetTradeCanonicalIdentityShape,
      side: z.literal("buy"),
      amount: AssetTradeCanonicalBuyAmountSchema,
      planBasis: AssetTradePlanBasisSchema,
    })
    .strict(),
);

export const AssetTradeCreateRequestSchema = lazySchema(() =>
  z.union([
    AssetTradeConcreteCreateRequestSchema,
    AssetTradeCanonicalBuyCreateRequestSchema,
  ]),
);

export const AssetTradeDisabledReasonSchema = lazySchema(() =>
  z.enum([
    /** Hold-adjusted balance does not cover the trade (and no funding applies). */
    "insufficient_balance",
    /** Complete execution cash evidence is below the requested gross amount. */
    "insufficient_funding_sources",
    /** Cash is not proven short; funding routes or planning are unavailable. */
    "funding_unavailable",
    /** Nothing spendable to sell. */
    "nothing_to_sell",
    /** The order is below the venue's minimum notional. */
    "below_minimum",
    "market_closed",
    "market_paused",
    /** The venue returned a quote it cannot execute. */
    "quote_not_executable",
    /**
     * The portfolio's account could not be read, so its signing checks did
     * not run. Retryable: the next preview reads it again.
     */
    "account_unavailable",
    /** A canonical preview without an amount: resolved only, nothing quoted. */
    "amount_required",
  ]),
);

export const AssetTradeResolvedRouteRepresentationSchema = lazySchema(() =>
  z.enum([
    "native_asset",
    "primary_token",
    "wrapped_token",
    "bridged_token",
    "issuer_representation",
    "receipt_token",
    "staking_derivative",
    "synthetic_token",
  ]),
);

export const AssetTradeResolvedRouteCandidateOutcomeSchema = lazySchema(() =>
  z.enum([
    /** The route this preview priced and signed. */
    "selected",
    /** The venue refused to quote it (no liquidity, token unavailable). */
    "quote_refused",
    /** Previewed, but the planner could not fund its chain; a later candidate was answered. */
    "funding_insufficient",
    /** Ranked below the selected route; never quoted. */
    "not_attempted",
  ]),
);

/**
 * How a canonical buy was resolved: the token and chain the engine chose,
 * why it could pay there, and the candidates it ranked. Present exactly
 * when the preview was asked for a canonical asset.
 */
export const AssetTradeResolvedRouteSchema = lazySchema(() =>
  z
    .object({
      assetCanonicalId: z.string().min(1),
      assetId: z.string().min(1),
      /** `public.assets.id` of the resolved token, for catalog lookups. */
      assetDbId: z.number().int().positive(),
      chainId: z.number().int().positive(),
      symbol: z.string().min(1),
      decimals: z.number().int().nonnegative(),
      representation: AssetTradeResolvedRouteRepresentationSchema.nullable(),
      issuerSlug: z.string().nullable(),
      chainTier: z.enum(["cheap", "expensive"]),
      /**
       * `covered`: the chain's own settlement cash pays for the order;
       * `funded`: the funding planner moves cash there first; `unknown`: no
       * amount was given, so coverage was not assessed.
       */
      coverage: z.enum(["covered", "funded", "unknown"]),
      candidates: z.array(
        z
          .object({
            assetId: z.string().min(1),
            chainId: z.number().int().positive(),
            symbol: z.string().min(1),
            outcome: AssetTradeResolvedRouteCandidateOutcomeSchema,
            reason: z.string().nullable(),
          })
          .strict(),
      ),
    })
    .strict(),
);

export const AssetTradeMarketWindowSchema = lazySchema(() =>
  z
    .object({
      /**
       * `open` for venues that trade continuously; `unknown` when the issuer
       * status could not be read (create re-checks and fails closed).
       */
      state: z.enum(["open", "closed", "paused", "unknown"]),
      nextOpenAt: z.string().nullable(),
      affectedAssets: z.array(z.string()),
    })
    .strict(),
);

export const AssetTradeFundingPreviewSchema = lazySchema(() =>
  z
    .object({
      /** Plan availability; disabledReasons distinguishes missing cash from unavailable routes. */
      status: z.enum(["planned", "insufficient"]),
      buyingPowerRaw: NonNegativeIntegerAmountSchema,
      /** The funding plan, or the best partial plan when insufficient. */
      plan: SpecDrivenBuyFundPlanWireSchema.nullable(),
      explanation: SpecDrivenBuyFundingExplanationWireSchema,
      /** Rail cost between the gross debit and what reaches the trade. */
      feeRaw: NonNegativeIntegerAmountSchema,
      /** Gross cash the plan leaves where it is. */
      retainedRaw: NonNegativeIntegerAmountSchema,
      /** Cash the owner would need to deposit when the plan is partial. */
      depositShortfallRaw: NonNegativeIntegerAmountSchema,
    })
    .strict(),
);

export const AssetTradePreviewResponseSchema = lazySchema(() =>
  z
    .object({
      flow: z.literal("asset_trade"),
      portfolioId: z.string().min(1),
      assetId: z.string().min(1),
      side: AssetTradeSideSchema,
      chainId: z.number().int().positive(),
      cashAsset: z
        .object({
          assetId: z.string().min(1),
          symbol: z.string().min(1),
          decimals: z.number().int().nonnegative(),
        })
        .strict(),
      /** Buys: gross cash debited and the cash that reaches the trade. */
      spend: z
        .object({
          grossRaw: PositiveIntegerAmountSchema,
          deliverableRaw: PositiveIntegerAmountSchema,
        })
        .strict()
        .nullable(),
      quote: ExecutionMarketOrderQuotePreviewSchema.nullable(),
      funding: AssetTradeFundingPreviewSchema.nullable(),
      marketWindow: AssetTradeMarketWindowSchema,
      minimum: z
        .object({
          cashAssetId: z.string().min(1),
          minimumRaw: PositiveIntegerAmountSchema,
          requestedRaw: NonNegativeIntegerAmountSchema.nullable(),
        })
        .strict()
        .nullable(),
      /** Hold-adjusted balance of what the trade sells (cash for buys). */
      availability: z
        .object({
          requiredRaw: NonNegativeIntegerAmountSchema,
          availableRaw: NonNegativeIntegerAmountSchema,
          heldRaw: NonNegativeIntegerAmountSchema,
        })
        .strict(),
      disabledReasons: z.array(AssetTradeDisabledReasonSchema),
      canSubmit: z.boolean(),
      expiresAt: z.number().int().nonnegative(),
      /** Always present when `canSubmit`; null otherwise. */
      planBasis: AssetTradePlanBasisSchema.nullable(),
      /** Present exactly when the request named a canonical asset. */
      resolvedRoute: AssetTradeResolvedRouteSchema.optional(),
    })
    .strict()
    .superRefine((preview, ctx) => {
      if (preview.canSubmit !== (preview.planBasis !== null)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "planBasis is present exactly when canSubmit",
          path: ["planBasis"],
        });
      }
      if (preview.canSubmit && preview.disabledReasons.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "a submittable preview carries no disabled reasons",
          path: ["disabledReasons"],
        });
      }
    }),
);

export const AssetTradeOperationResponseSchema = lazySchema(() =>
  ExecutionOperationResponseSchema.extend({
    flow: z.literal("asset_trade"),
    side: AssetTradeSideSchema,
    provider: z.string().min(1),
    funding: ExecutionSpecDrivenBuyCreatedFundingSchema.nullable(),
    live: LiveDescriptorSchema,
    portfolioLive: LiveDescriptorSchema,
  }),
);

/**
 * `GET /execution/trades/:operationId/funding`: the funding plan a funded buy
 * persisted at admission, for clients that lost the create response. `null`
 * when the operation moved no cash first.
 */
export const AssetTradeFundingRecoveryResponseSchema = lazySchema(() =>
  z
    .object({
      funding: ExecutionSpecDrivenBuyCreatedFundingSchema.nullable(),
    })
    .strict(),
);

/**
 * `POST /execution/trades/max`: the most the engine would accept as this
 * trade's `amount` right now. Callers name scope only; the engine reads every
 * balance itself.
 */
export const AssetTradeMaxRequestSchema = lazySchema(() =>
  z
    .object({
      portfolioId: ExecutionPortfolioIdSchema,
      side: AssetTradeSideSchema,
      assetId: AssetTradeAssetIdSchema,
    })
    .strict(),
);

/** Why a buy Max is zero. */
export const AssetTradeBuyMaxReasonSchema = lazySchema(() =>
  z.enum([
    /** No spendable cash in any of the portfolio's accounts. */
    "no_cash",
    /** Cash is moving between accounts (a bridge not yet proven landed). */
    "cash_in_transit",
    /** The portfolio has no execution account on the asset's chain. */
    "no_account",
  ]),
);

/** Why a sell Max is zero. */
export const AssetTradeSellMaxReasonSchema = lazySchema(() =>
  z.enum([
    /** The execution account holds none of the asset. */
    "nothing_to_sell",
    /** Every unit held is reserved by an open hold. */
    "fully_reserved",
  ]),
);

const assetTradeMaxObservationShape = lazySchema(
  () =>
    ({
      portfolioId: z.string().min(1),
      assetId: z.string().min(1),
      capturedAt: z.iso.datetime(),
      /** Server-clock deadline; compare only with `capturedAt`. */
      expiresAt: z.iso.datetime(),
    }) satisfies z.ZodRawShape,
);

/**
 * `amount` uses the same keys as the trade request, in the same units: a buy
 * Max is `cashRaw` of `cashAsset` (the cash this trade settles in), a sell Max
 * is `assetRaw` of `assetId`. `reason` is set exactly when the amount is zero.
 * Admission gates that do not depend on balance (market hours, minimums,
 * quote executability) stay with the preview.
 */
export const AssetTradeMaxResponseSchema = lazySchema(() =>
  z
    .discriminatedUnion("side", [
      z
        .object({
          ...assetTradeMaxObservationShape,
          side: z.literal("buy"),
          cashAsset: z
            .object({
              assetId: z.string().min(1),
              symbol: z.string().min(1),
              decimals: z.number().int().nonnegative(),
            })
            .strict(),
          amount: z
            .object({ cashRaw: NonNegativeIntegerAmountSchema })
            .strict(),
          reason: AssetTradeBuyMaxReasonSchema.nullable(),
        })
        .strict(),
      z
        .object({
          ...assetTradeMaxObservationShape,
          side: z.literal("sell"),
          amount: z
            .object({ assetRaw: NonNegativeIntegerAmountSchema })
            .strict(),
          reason: AssetTradeSellMaxReasonSchema.nullable(),
        })
        .strict(),
    ])
    .superRefine((max, ctx) => {
      const raw =
        "cashRaw" in max.amount ? max.amount.cashRaw : max.amount.assetRaw;
      if ((raw === "0") !== (max.reason !== null)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "reason is present exactly when the maximum is zero",
          path: ["reason"],
        });
      }
      if (Date.parse(max.expiresAt) <= Date.parse(max.capturedAt)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "expiresAt must follow capturedAt",
          path: ["expiresAt"],
        });
      }
    }),
);

export type AssetTradeSide = z.infer<typeof AssetTradeSideSchema>;
export type AssetTradeMaxRequest = z.infer<typeof AssetTradeMaxRequestSchema>;
export type AssetTradeMaxResponse = z.infer<typeof AssetTradeMaxResponseSchema>;
export type AssetTradeBuyMaxReason = z.infer<
  typeof AssetTradeBuyMaxReasonSchema
>;
export type AssetTradeSellMaxReason = z.infer<
  typeof AssetTradeSellMaxReasonSchema
>;
export type AssetTradePlanBasis = z.infer<typeof AssetTradePlanBasisSchema>;
export type AssetTradePlanBasisResolution = z.infer<
  typeof AssetTradePlanBasisResolutionSchema
>;
export type AssetTradePreviewRequest = z.infer<
  typeof AssetTradePreviewRequestSchema
>;
export type AssetTradeConcretePreviewRequest = z.infer<
  typeof AssetTradeConcretePreviewRequestSchema
>;
export type AssetTradeCanonicalBuyPreviewRequest = z.infer<
  typeof AssetTradeCanonicalBuyPreviewRequestSchema
>;
export type AssetTradeCreateRequest = z.infer<
  typeof AssetTradeCreateRequestSchema
>;
export type AssetTradeConcreteCreateRequest = z.infer<
  typeof AssetTradeConcreteCreateRequestSchema
>;
export type AssetTradeCanonicalBuyCreateRequest = z.infer<
  typeof AssetTradeCanonicalBuyCreateRequestSchema
>;
export type AssetTradeResolvedRoute = z.infer<
  typeof AssetTradeResolvedRouteSchema
>;
export type AssetTradeResolvedRouteRepresentation = z.infer<
  typeof AssetTradeResolvedRouteRepresentationSchema
>;

/** True for a request that names a canonical asset rather than a token. */
export const isAssetTradeCanonicalRequest = <
  T extends { readonly assetCanonicalId?: string; readonly assetId?: string },
>(
  request: T,
): request is Extract<T, { readonly assetCanonicalId: string }> =>
  typeof request.assetCanonicalId === "string";
export type AssetTradeDisabledReason = z.infer<
  typeof AssetTradeDisabledReasonSchema
>;
export type AssetTradeMarketWindow = z.infer<
  typeof AssetTradeMarketWindowSchema
>;
export type AssetTradeFundingPreview = z.infer<
  typeof AssetTradeFundingPreviewSchema
>;
export type AssetTradePreviewResponse = z.infer<
  typeof AssetTradePreviewResponseSchema
>;
export type AssetTradeOperationResponse = z.infer<
  typeof AssetTradeOperationResponseSchema
>;
export type AssetTradeFunding = z.infer<
  typeof ExecutionSpecDrivenBuyCreatedFundingSchema
>;
export type AssetTradeFundingRecoveryResponse = z.infer<
  typeof AssetTradeFundingRecoveryResponseSchema
>;
